import React, { useState, useMemo, useEffect } from 'react';
import { canUseStorage } from '../../hooks/useCookieConsent';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Download, CheckSquare, Square, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { apiClient } from '../../lib/api';

/**
 * ExportDialog Component
 *
 * Dialog for selecting fields to export and triggering CSV download.
 * Features:
 * - Grouped field selection by category
 * - Select All / Deselect All controls
 * - LocalStorage persistence of selections
 * - Permission-aware field display
 * - Direct CSV download (no background processing)
 */
export function ExportDialog({
  open,
  onClose,
  availableFields,
  currentFilters,
  filteredCount,
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [selectedFields, setSelectedFields] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // Translate field label if translation exists, otherwise use backend label
  const getFieldLabel = (field) => {
    const translationKey = `export.fields.${field.name}`;
    const translated = t(translationKey);
    // If translation key not found, t() returns the key itself
    if (translated !== translationKey) {
      return translated;
    }
    // Fall back to backend label
    return field.label;
  };

  // Group fields by category/group
  const fieldGroups = useMemo(() => {
    const groups = {};

    availableFields.forEach((field) => {
      const groupName = field.group || 'Other';
      if (!groups[groupName]) {
        groups[groupName] = {
          name: groupName,
          label: t(`export.groups.${groupName.toLowerCase()}`, groupName),
          fields: [],
        };
      }
      // Add field with translated label
      groups[groupName].fields.push({
        ...field,
        displayLabel: getFieldLabel(field)
      });
    });

    // Sort fields within each group alphabetically
    Object.values(groups).forEach((group) => {
      group.fields.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
    });

    return Object.values(groups);
  }, [availableFields, t]);

  // Load saved selections from localStorage or use defaults
  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem('export_field_selection');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Filter to only include fields user still has access to
          const validFields = parsed.filter((name) =>
            availableFields.some((f) => f.name === name)
          );
          setSelectedFields(validFields);
        } catch (e) {
          // If parsing fails, select default fields
          setSelectedFields(getDefaultFields());
        }
      } else {
        // First time - select common fields
        setSelectedFields(getDefaultFields());
      }
    }
  }, [open, availableFields]);

  const getDefaultFields = () => {
    // Select common, non-sensitive fields by default
    const defaultFieldNames = [
      'student_id',
      'first_name',
      'last_name',
      'birthday',
      'gender',
      'parent_email',
      'parent_phone',
      'course',
      'house',
      'house_id',
      'has_friends',
      'friend_1_id',
      'friend_1_name',
      'friend_2_id',
      'friend_2_name',
    ];
    return availableFields
      .filter((f) => defaultFieldNames.includes(f.name) && !f.sensitive)
      .map((f) => f.name);
  };

  const toggleField = (fieldName) => {
    setSelectedFields((prev) =>
      prev.includes(fieldName)
        ? prev.filter((name) => name !== fieldName)
        : [...prev, fieldName]
    );
  };

  const toggleGroup = (group) => {
    const groupFieldNames = group.fields.map((f) => f.name);
    const allSelected = groupFieldNames.every((name) =>
      selectedFields.includes(name)
    );

    if (allSelected) {
      // Deselect all in group
      setSelectedFields((prev) =>
        prev.filter((name) => !groupFieldNames.includes(name))
      );
    } else {
      // Select all in group
      setSelectedFields((prev) => [...new Set([...prev, ...groupFieldNames])]);
    }
  };

  const selectAll = () => {
    setSelectedFields(availableFields.map((f) => f.name));
  };

  const deselectAll = () => {
    setSelectedFields([]);
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      toast({
        title: t('export.noFieldsSelected'),
        variant: 'destructive',
      });
      return;
    }

    // Save selection for next time (only if functional cookies accepted)
    if (canUseStorage()) {
      localStorage.setItem('export_field_selection', JSON.stringify(selectedFields));
    }

    setIsExporting(true);
    try {
      const response = await apiClient.post('/api/students/export/csv/', {
        field_names: selectedFields,
        filters: currentFilters,
      }, {
        responseType: 'blob',
        timeout: 120000, // 2 minutes for large exports
      });

      // Trigger download
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `students_export_${Date.now()}.csv`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: t('export.success', { count: filteredCount }),
        variant: 'success',
      });
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: t('export.error'),
        description: error.response?.data?.detail || error.message || undefined,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>{t('export.title')}</DialogTitle>
          <DialogDescription>
            {t('export.description', { count: filteredCount })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selection controls */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {t('export.selectedCount', {
                selected: selectedFields.length,
                total: availableFields.length,
              })}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAll}
              >
                <CheckSquare className="h-4 w-4 mr-1" />
                {t('export.selectAll')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={deselectAll}
              >
                <Square className="h-4 w-4 mr-1" />
                {t('export.deselectAll')}
              </Button>
            </div>
          </div>

          {/* Field groups */}
          <div className="h-[400px] border rounded-md p-4 overflow-y-auto">
            <div className="space-y-6">
              {fieldGroups.map((group) => {
                const groupFieldNames = group.fields.map((f) => f.name);
                const selectedInGroup = groupFieldNames.filter((name) =>
                  selectedFields.includes(name)
                ).length;
                const allSelected =
                  selectedInGroup === groupFieldNames.length;
                const someSelected = selectedInGroup > 0 && !allSelected;

                return (
                  <div key={group.name} className="space-y-3">
                    {/* Group header with checkbox */}
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Checkbox
                        id={`group-${group.name}`}
                        checked={allSelected}
                        onCheckedChange={() => toggleGroup(group)}
                        className={someSelected ? 'opacity-50' : ''}
                      />
                      <Label
                        htmlFor={`group-${group.name}`}
                        className="text-sm font-semibold cursor-pointer flex-1"
                      >
                        {group.label}
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                          ({selectedInGroup}/{groupFieldNames.length})
                        </span>
                      </Label>
                    </div>

                    {/* Field checkboxes in 2-column grid */}
                    <div className="grid grid-cols-2 gap-3 ml-6">
                      {group.fields.map((field) => (
                        <div
                          key={field.name}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            id={`field-${field.name}`}
                            checked={selectedFields.includes(field.name)}
                            onCheckedChange={() => toggleField(field.name)}
                          />
                          <Label
                            htmlFor={`field-${field.name}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {field.displayLabel}
                            {field.sensitive && (
                              <span className="ml-1 text-xs text-amber-600">
                                {t('export.sensitive')}
                              </span>
                            )}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || selectedFields.length === 0}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {isExporting ? t('export.exporting') : t('export.exportCSV')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
